import {
  AI_AGENT_LABELS,
  formatScopePaths,
  formatLabel,
  formatModelLabel,
  type LaunchState,
  type ScanAcceptedResponse,
  type SelectedAiModel
} from './workflowShared';

interface WebPentestCoverageOption {
  id: string;
  title: string;
  category?: string;
}

interface WebPentestScopeStepProps {
  coverageOptions: WebPentestCoverageOption[];
  selectedCoverage: string[];
  onToggleCoverage: (coverage: string) => void;
}

interface WebPentestReviewStepProps {
  orderedSelectedAiModels: SelectedAiModel[];
  selectedCoverageLabels: string[];
  target: string;
  repository: string;
  branch: string;
  scopePaths: string[];
  authContext: string;
  testAccountContext: string;
  scopeNotes: string;
  scanResponse: ScanAcceptedResponse | null;
  launchState: LaunchState;
  launchError: string;
  onViewRun: (runId: string) => void;
}

function WebPentestScopeStep({
  coverageOptions,
  selectedCoverage,
  onToggleCoverage
}: WebPentestScopeStepProps) {
  const selectedCoverageSet = new Set(selectedCoverage);
  const coverageGroups = coverageOptions.reduce<Array<{ title: string; options: WebPentestCoverageOption[] }>>(
    (groups, option) => {
      const title = option.category ?? 'Other';
      const existingGroup = groups.find((group) => group.title === title);

      if (existingGroup) {
        existingGroup.options.push(option);
      } else {
        groups.push({ title, options: [option] });
      }

      return groups;
    },
    []
  );

  return (
    <>
      <div className="step-heading">
        <h1>Select pentest coverage</h1>
        <p>Choose the security areas the AI models should prioritize for this web and API assessment.</p>
      </div>

      <div className="web-coverage-sections">
        {coverageGroups.map((group) => (
          <section className="web-coverage-section" key={group.title}>
            <h2>{group.title}</h2>
            <div className="skill-list">
              {group.options.map((coverage) => {
                const isSelected = selectedCoverageSet.has(coverage.id);

                return (
                  <button
                    aria-pressed={isSelected}
                    className={`skill-chip ${isSelected ? 'selected' : ''}`}
                    key={coverage.id}
                    type="button"
                    onClick={() => onToggleCoverage(coverage.id)}
                  >
                    <span className="checkbox-mark" aria-hidden="true" />
                    <span className="skill-chip-content">
                      <span className="skill-chip-title">{coverage.title}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </>
  );
}

function WebPentestReviewStep({
  orderedSelectedAiModels,
  selectedCoverageLabels,
  target,
  repository,
  branch,
  scopePaths,
  authContext,
  testAccountContext,
  scopeNotes,
  scanResponse,
  launchState,
  launchError,
  onViewRun
}: WebPentestReviewStepProps) {
  if (launchState === 'accepted') {
    const runId = scanResponse?.runId;

    return (
      <div className="pricing-launch-complete">
        <div className="pricing-launch-run-card" role="status" aria-label="Pentest run created">
          <div className="pricing-launch-run-id">
            <span>Run ID</span>
            <strong>{runId || 'Unavailable'}</strong>
          </div>

          {runId ? (
            <a
              className="primary-button pricing-launch-run-link"
              href={`/run/${encodeURIComponent(runId)}`}
              onClick={(event) => {
                event.preventDefault();
                onViewRun(runId);
              }}
            >
              View run
            </a>
          ) : (
            <button className="primary-button pricing-launch-run-link" type="button" disabled>
              View run
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="step-heading pricing-heading">
        <h1>Review web pentest setup</h1>
        <p>Confirm the live target, source repository, coverage, and context before launching the pentest.</p>
      </div>

      <div className="pricing-panel web-review-panel">
        <dl className="pricing-breakdown">
          <div>
            <dt>Target</dt>
            <dd>{target || 'Not set'}</dd>
          </div>
          <div>
            <dt>Repository</dt>
            <dd>{repository || 'Not set'}</dd>
          </div>
          <div>
            <dt>Branch</dt>
            <dd>{branch || 'Not set'}</dd>
          </div>
          <div>
            <dt>Source scope</dt>
            <dd>{formatScopePaths(scopePaths)}</dd>
          </div>
          <div>
            <dt>Coverage</dt>
            <dd>{selectedCoverageLabels.length ? selectedCoverageLabels.join(', ') : 'None selected'}</dd>
          </div>
          <div>
            <dt>AI models</dt>
            <dd>
              {orderedSelectedAiModels.length ? (
                <span className="pricing-model-costs">
                  {orderedSelectedAiModels.map(({ agent, model }) => (
                    <span key={`${agent}-${model}`}>
                      {formatLabel(agent, AI_AGENT_LABELS)} / {formatModelLabel(model)}
                    </span>
                  ))}
                </span>
              ) : 'None selected'}
            </dd>
          </div>
          <div>
            <dt>Auth</dt>
            <dd>{authContext.trim() || 'Not provided'}</dd>
          </div>
          <div>
            <dt>Accounts</dt>
            <dd>{testAccountContext.trim() || 'Not provided'}</dd>
          </div>
          <div>
            <dt>Scope notes</dt>
            <dd>{scopeNotes.trim() || 'Not provided'}</dd>
          </div>
        </dl>
      </div>

      {launchState === 'error' && (
        <div className="status-box error" role="alert">
          <span>Pentest request failed.</span>
          <span>{launchError}</span>
        </div>
      )}
    </>
  );
}

export {
  type WebPentestCoverageOption,
  WebPentestReviewStep,
  WebPentestScopeStep
};
